import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

type ThemePreference = 'dark' | 'light' | 'system'
type ResolvedTheme = 'dark' | 'light'

export type Theme = ThemePreference

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

interface ThemeContextType {
  theme: ThemePreference
  setTheme: (theme: ThemePreference) => void
  toggleTheme: () => void
  isDark: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const STORAGE_KEY = 'lukhas_id_theme'

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themePreference, setThemePreference] = useState<ThemePreference>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(STORAGE_KEY) as ThemePreference
        if (saved === 'light' || saved === 'dark' || saved === 'system') {
          return saved
        }
      } catch {
        // localStorage unavailable (e.g. private browsing); fall back to default
      }
    }
    return 'dark'
  })

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(STORAGE_KEY) as ThemePreference
        if (saved === 'light' || saved === 'dark') return saved
      } catch {
        // localStorage unavailable; fall back to system theme
      }
    }
    return getSystemTheme()
  })

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, themePreference)
    } catch {
      // localStorage unavailable or quota exceeded; skip persisting theme
    }
    const resolved = themePreference === 'system' ? getSystemTheme() : themePreference
    setResolvedTheme(resolved)
    document.documentElement.classList.remove('dark', 'light')
    document.documentElement.classList.add(resolved)
    document.documentElement.style.colorScheme = resolved
  }, [themePreference])

  useEffect(() => {
    if (themePreference !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => {
      const newTheme = e.matches ? 'dark' : 'light'
      setResolvedTheme(newTheme)
      document.documentElement.classList.remove('light', 'dark')
      document.documentElement.classList.add(newTheme)
      document.documentElement.style.colorScheme = newTheme
    }

    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [themePreference])

  const setTheme = (newTheme: ThemePreference) => {
    setThemePreference(newTheme)
  }

  const toggleTheme = () => {
    setThemePreference(prev => {
      if (prev === 'system') {
        return resolvedTheme === 'dark' ? 'light' : 'dark'
      }
      return prev === 'dark' ? 'light' : 'dark'
    })
  }

  return (
    <ThemeContext.Provider value={{
      theme: themePreference,
      setTheme,
      toggleTheme,
      isDark: resolvedTheme === 'dark',
    }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
